import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { getStats } from "../../store/actions/generic";

import { GET_STATS } from "../../store/actionTypes";
import MaterialBackDrop from "../../components/ui/loading/materialBackDrop";

const CLASS_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const num = (value) => (isNaN(value) || value == null ? 0 : Number(value));
const formatNumber = (value) => num(value).toLocaleString("tr-TR");

const styles = {
  page: { display: "flex", flexDirection: "column", gap: 24, width: "100%" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderRadius: 12,
    color: "#222",
  },
  title: { margin: 0, fontSize: 24, fontWeight: 600 },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: "#666" },
  tiles: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
  },
  tile: { padding: "16px 20px", borderRadius: 12 },
  tileLabel: { fontSize: 13, color: "#666", marginBottom: 6 },
  tileValue: { fontSize: 30, fontWeight: 700, color: "#222", lineHeight: 1.1 },
  tileNote: { fontSize: 13, color: "#666", marginTop: 6 },
  card: { borderRadius: 12, overflow: "hidden" },
  cardTitle: {
    padding: "16px 20px 8px",
    fontSize: 16,
    fontWeight: 600,
    color: "#222",
  },
  headCell: { fontWeight: 600, color: "#555", whiteSpace: "nowrap" },
  totalCell: { fontWeight: 700, borderTop: "2px solid #ddd" },
};

const StatTile = ({ label, value, note }) => (
  <Paper elevation={0} style={styles.tile}>
    <div style={styles.tileLabel}>{label}</div>
    <div style={styles.tileValue}>{value}</div>
    {note && <div style={styles.tileNote}>{note}</div>}
  </Paper>
);

const Stats = () => {
  const user = useSelector((state) => state.auth && state.auth.user);
  const stats = useSelector((state) => state.generic.stats);
  const loadingStats = useSelector((state) => state.loading[GET_STATS]);

  const dispatch = useDispatch();

  const fetchStats = () => {
    if (user && user.role == 2) dispatch(getStats());
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (!stats) return loadingStats ? <MaterialBackDrop /> : null;

  const rows = CLASS_IDS.map((classId) => ({
    classId,
    registersToday: num(stats.registers_of_today[classId]),
    registersAll: num(stats.registers_of_all_days[classId]),
    paidsToday: num(stats.paids_of_today[classId]),
    paidsAll: num(stats.paids_of_all_days[classId]),
  }));

  const studentTotals = rows.reduce(
    (t, r) => ({
      registersToday: t.registersToday + r.registersToday,
      registersAll: t.registersAll + r.registersAll,
      paidsToday: t.paidsToday + r.paidsToday,
      paidsAll: t.paidsAll + r.paidsAll,
    }),
    { registersToday: 0, registersAll: 0, paidsToday: 0, paidsAll: 0 },
  );

  const coachesToday = num(stats.registers_of_today.coach);
  const coachesAll = num(stats.registers_of_all_days.coach);

  const todayNote = (value) =>
    value > 0 ? `Bugün +${formatNumber(value)}` : "Bugün henüz yok";

  return (
    <div style={styles.page}>
      {loadingStats && <MaterialBackDrop />}

      <Paper elevation={0} style={styles.header}>
        <div>
          <h2 style={styles.title}>İstatistikler</h2>
          <p style={styles.subtitle}>
            {new Date().toLocaleDateString("tr-TR", {
              timeZone: "Europe/Istanbul",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}{" "}
            itibarıyla kayıt ve ödeme durumu
          </p>
        </div>
        <Button variant="contained" color="secondary" onClick={fetchStats}>
          Yenile
        </Button>
      </Paper>

      <div style={styles.tiles}>
        <StatTile
          label="Kayıtlı öğrenci"
          value={formatNumber(studentTotals.registersAll)}
          note={todayNote(studentTotals.registersToday)}
        />
        <StatTile
          label="Ödeme yapan öğrenci"
          value={formatNumber(studentTotals.paidsAll)}
          note={todayNote(studentTotals.paidsToday)}
        />
        <StatTile
          label="Kayıtlı öğretmen"
          value={formatNumber(coachesAll)}
          note={todayNote(coachesToday)}
        />
        <StatTile
          label="Kayıtlı okul"
          value={formatNumber(stats.school_count)}
          note={`${formatNumber(stats.school_count_without_coach)} okulun öğretmeni yok`}
        />
      </div>

      <TableContainer component={Paper} elevation={0} style={styles.card}>
        <div style={styles.cardTitle}>Sınıflara göre öğrenciler</div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={styles.headCell}>Sınıf</TableCell>
              <TableCell style={styles.headCell} align="right">
                Bugün kayıt
              </TableCell>
              <TableCell style={styles.headCell} align="right">
                Toplam kayıt
              </TableCell>
              <TableCell style={styles.headCell} align="right">
                Bugün ödeme
              </TableCell>
              <TableCell style={styles.headCell} align="right">
                Toplam ödeme
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.classId} hover>
                <TableCell>{r.classId}. Sınıf</TableCell>
                <TableCell align="right">{formatNumber(r.registersToday)}</TableCell>
                <TableCell align="right">{formatNumber(r.registersAll)}</TableCell>
                <TableCell align="right">{formatNumber(r.paidsToday)}</TableCell>
                <TableCell align="right">{formatNumber(r.paidsAll)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell style={styles.totalCell}>Toplam</TableCell>
              <TableCell style={styles.totalCell} align="right">
                {formatNumber(studentTotals.registersToday)}
              </TableCell>
              <TableCell style={styles.totalCell} align="right">
                {formatNumber(studentTotals.registersAll)}
              </TableCell>
              <TableCell style={styles.totalCell} align="right">
                {formatNumber(studentTotals.paidsToday)}
              </TableCell>
              <TableCell style={styles.totalCell} align="right">
                {formatNumber(studentTotals.paidsAll)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default Stats;
